/**
 * Holographic stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const holographicStickersPack: ToilePack = {
    slug: 'holographic-stickers',
    category: 'sticker',
    name: 'Holographic stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'moon', label: 'Moon', subject: 'A crescent moon with a small face, no lettering'},
        {key: 'butterfly', label: 'Butterfly', subject: 'A butterfly with wings open flat, no lettering'},
        {key: 'star', label: 'Lettered star', subject: 'A chunky star lettered "WOW"'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['as a heart instead', 'in silver only', 'for a laptop lid'],
    directionHint: 'try: as a heart instead',
    seo: {
        title: 'Holographic Stickers: Moon, Butterfly, Star Sheen',
        metaDescription: 'Nine holographic stickers — a holographic moon, a holographic butterfly, a lettered holographic star — in holo, puffy, and clear finishes.',
        h1: 'Holographic stickers: moon, butterfly, and star with a rainbow sheen',
        tagline: 'Nine holographic stickers, three shapes, three finishes each.',
        intro: 'The finish as the subject: a holographic moon, a holographic butterfly, a holographic lettered star, where the rainbow sheen is the whole point. Alongside the holo finish, each also comes in puffy and clear so you can compare the effect side by side. Ask for a different shape, and the same three finishes carry it.',
        useCases: [
            'decorating a laptop or water bottle with a rainbow-sheen sticker',
            'a phone case accent sticker',
            'a journal or planner shine accent',
            'a printable holographic sticker sheet',
            'a gift sticker set for a sparkle-loving friend',
        ],
        faq: [
            {q: 'Do these print with a real holographic shine, or is it drawn on?', a: 'The rainbow sheen is illustrated into the design; a genuinely reflective finish needs printing onto real holographic vinyl sticker paper.'},
            {q: "What's the difference between holo, puffy, and clear here?", a: 'Holo is the rainbow-sheen look, puffy is a domed 3D look, and clear is a transparent background with only the design printed — all three are included for every shape.'},
            {q: 'Can I get a different shape than moon, butterfly, or star?', a: 'Yes — the direction field takes any shape, rendered in the same three finishes.'},
            {q: 'What sticker paper should I use to get a real holographic effect?', a: 'Look for holographic vinyl sticker paper made for inkjet or laser printers — most craft and print shops carry it.'},
            {q: 'Can I print these for a small sticker shop?', a: 'Personal use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
